'use client';

import * as React from 'react';
import toast from 'react-hot-toast';
import type { Room } from 'livekit-client';
import { isVideoTrack, LocalVideoTrack, Track } from 'livekit-client';
import {
  getEffectiveVideoQualityProfile,
  getVideoQualityLabel,
  getVideoQualityRoomCapture,
  readVideoQualityProfile,
  type VideoQualityProfile,
  VIDEO_QUALITY_CHANGED_EVENT,
  writeVideoQualityProfile,
} from './video-quality-profile';
import { readEconomyModeEnabled } from './economyMode';

type VideoQualityContextValue = {
  videoQualityProfile: VideoQualityProfile;
  setVideoQualityProfile: (profile: VideoQualityProfile) => void;
};

const VideoQualityContext = React.createContext<VideoQualityContextValue | null>(null);

export function VideoQualityProvider({
  children,
  initialProfile,
}: {
  children: React.ReactNode;
  initialProfile?: VideoQualityProfile;
}) {
  const [videoQualityProfile, setProfileState] = React.useState<VideoQualityProfile>(
    initialProfile ?? 'medium',
  );

  React.useEffect(() => {
    setProfileState(initialProfile ?? readVideoQualityProfile());
    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<{ profile?: VideoQualityProfile }>).detail;
      if (detail?.profile) {
        setProfileState(detail.profile);
      } else {
        setProfileState(readVideoQualityProfile());
      }
    };
    window.addEventListener(VIDEO_QUALITY_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(VIDEO_QUALITY_CHANGED_EVENT, onChange);
  }, [initialProfile]);

  const setVideoQualityProfile = React.useCallback((profile: VideoQualityProfile) => {
    writeVideoQualityProfile(profile);
    setProfileState(profile);
    toast(`Qualidade da câmera: ${getVideoQualityLabel(profile)}`, {
      icon: '📹',
      duration: 3500,
    });
  }, []);

  const value = React.useMemo(
    () => ({ videoQualityProfile, setVideoQualityProfile }),
    [videoQualityProfile, setVideoQualityProfile],
  );

  return (
    <VideoQualityContext.Provider value={value}>{children}</VideoQualityContext.Provider>
  );
}

export function useVideoQuality(): VideoQualityContextValue {
  const ctx = React.useContext(VideoQualityContext);
  if (!ctx) {
    throw new Error('useVideoQuality must be used within VideoQualityProvider');
  }
  return ctx;
}

export function useOptionalVideoQuality(): VideoQualityContextValue | null {
  return React.useContext(VideoQualityContext);
}

/** Republica a câmera com a resolução do perfil escolhido. */
export async function applyVideoQualityToLocalCamera(
  room: Room | undefined,
  profile: VideoQualityProfile,
): Promise<void> {
  if (!room || room.state !== 'connected') {
    return;
  }

  const publication = room.localParticipant.getTrackPublication(Track.Source.Camera);
  const track = publication?.track;
  if (!track || !isVideoTrack(track)) {
    return;
  }

  const localTrack = track as LocalVideoTrack;
  const effective = getEffectiveVideoQualityProfile(profile, readEconomyModeEnabled());
  const { videoCaptureDefaults } = getVideoQualityRoomCapture(effective);

  try {
    await localTrack.restartTrack({
      resolution: videoCaptureDefaults.resolution,
    });
  } catch (error) {
    console.warn('[video-quality] restartTrack:', error);
  }
}
